import { Fragment, FunctionComponent, useMemo } from 'react';
import { Tooltip } from '@mui/material';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import type {
  KidFiguresRecordModel,
  KidFiguresScenarioPeriods,
} from '../../../../../api/kids/types';
import {
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlaces,
} from '../../../../../utilities/numberFormatters';
import { useFundAndShareClassName } from '../hooks/useFundAndShareClassName';
import {
  formatHoldingPeriodColumnLabel,
  formatKidFigureCalculatedTimestamp,
  formatKidFigureCalculationDate,
  getKidFiguresStatus,
  KID_FIGURES_STATUS_CLASS,
  KID_FIGURES_STATUS_LABEL,
  resolveHoldingPeriodYears,
  type KidFiguresStatus,
} from '../utils/kidFigureUtils';

const SCENARIO_LABELS: Record<string, string> = {
  stressed: 'Stress',
  unfavourable: 'Unfavourable',
  moderate: 'Moderate',
  favourable: 'Favourable',
};

const SCENARIO_ORDER = [
  'stressed',
  'unfavourable',
  'moderate',
  'favourable',
] as const;

const SRI_MAX = 7;

// TODO(figures-ui): Enrich this overview with metadata not yet returned by the figures API.
// - Currency: needed for total-cost labels (currently shown as plain numbers).
// - Holding period (years): partially resolved from fund/share-class model; confirm source of truth.
// - Investment amount: required for KID-style scenario headers (e.g. "Investment GBP 10,000").
//   This is currently hardcoded per client on the backend and will need a persisted client/fund
//   configuration before the UI can display it accurately.

function formatRiy(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return percentageToTwoDecimalPlaces(value);
}

function formatTotalCost(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatScenarioAmount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return addCommasToNumbersAndRound(value);
}

interface KidFiguresOverviewProps {
  figuresRecord: KidFiguresRecordModel | null | undefined;
  clientId?: string;
  fundId?: string;
  shareClassId?: string;
}

export function resolveKidFiguresStatus(
  figuresRecord: KidFiguresRecordModel | null | undefined,
  figuresRequireRecalculation = false,
  hasFigures?: boolean,
): KidFiguresStatus {
  const figuresExist = hasFigures ?? Boolean(figuresRecord);
  return getKidFiguresStatus(
    figuresExist,
    figuresRecord?.calculation_date,
    figuresRequireRecalculation,
  );
}

const KidFiguresOverview: FunctionComponent<KidFiguresOverviewProps> = ({
  figuresRecord,
  clientId,
  fundId,
  shareClassId,
}) => {
  const shouldResolveFundMetadata = Boolean(clientId && fundId);

  const { fundsData, shareClassData } = useFundAndShareClassName({
    clientId: clientId ?? '',
    fundId: fundId ?? '',
    shareClassId,
    pageKey: 'kid-overview-v2',
  });

  const matchedFund = useMemo(
    () => fundsData?.find((fund) => fund.fund_id === fundId),
    [fundsData, fundId],
  );

  const holdingPeriodColumnLabel = useMemo(
    () =>
      formatHoldingPeriodColumnLabel(
        shouldResolveFundMetadata
          ? resolveHoldingPeriodYears(matchedFund, shareClassData?.[0])
          : null,
      ),
    [matchedFund, shareClassData, shouldResolveFundMetadata],
  );

  const figuresData = figuresRecord?.data;
  const riyData = figuresData?.riy_data;
  const scenarioData = figuresData?.scenario_data;
  const sri = figuresData?.sri ?? null;

  return (
    <div className="rounded-md border border-gray-200">
      <div className="flex items-center justify-between gap-4 border-b border-gray-200 bg-gray-50 px-3 py-2.5">
        <SriIndicator sri={sri} />
        <Tooltip
          arrow
          placement="top"
          title={
            <div className="text-xs leading-snug">
              Figures are calculated as of the most recent reporting Friday (the
              calculation date).
              <br />
              Calculation timestamp:{' '}
              {formatKidFigureCalculatedTimestamp(
                figuresRecord?.last_updating_datetime,
              )}
            </div>
          }
        >
          <div className="flex cursor-default items-center gap-1 text-xs text-gray-500">
            <span className="text-gray-400">Calculation date</span>
            <span className="font-medium text-gray-700">
              {formatKidFigureCalculationDate(figuresRecord?.calculation_date)}
            </span>
            <HiOutlineInformationCircle className="h-3.5 w-3.5 text-gray-400" />
          </div>
        </Tooltip>
      </div>

      {riyData && (
        <FigureTable
          caption="Reduction in yield"
          columns={['1 year', holdingPeriodColumnLabel]}
          rows={[
            {
              label: 'RIY (per year)',
              values: [
                formatRiy(riyData['1yr_period']?.riy),
                formatRiy(riyData.holding_period?.riy),
              ],
            },
            {
              label: 'Total costs',
              values: [
                formatTotalCost(riyData['1yr_period']?.total_cost),
                formatTotalCost(riyData.holding_period?.total_cost),
              ],
            },
          ]}
        />
      )}

      {scenarioData && (
        <PerformanceScenariosTable
          scenarioData={scenarioData}
          oneYearColumnLabel="1 year"
          holdingPeriodColumnLabel={holdingPeriodColumnLabel}
        />
      )}
    </div>
  );
};

const PerformanceScenariosTable: FunctionComponent<{
  scenarioData: Record<string, KidFiguresScenarioPeriods | undefined>;
  oneYearColumnLabel: string;
  holdingPeriodColumnLabel: string;
}> = ({ scenarioData, oneYearColumnLabel, holdingPeriodColumnLabel }) => (
  <table className="w-full border-b border-gray-200 text-xs last:border-b-0">
    <thead>
      <tr className="text-gray-500">
        <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide">
          Performance scenarios
        </th>
        <th className="px-3 py-1.5 text-left font-medium" />
        <th className="px-3 py-1.5 text-right font-medium">
          {oneYearColumnLabel}
        </th>
        <th className="px-3 py-1.5 text-right font-medium">
          {holdingPeriodColumnLabel}
        </th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-100 text-gray-800">
      {SCENARIO_ORDER.map((key) => {
        const scenario = scenarioData[key];
        if (!scenario) return null;

        const oneYear = scenario['1yr_period'];
        const holding = scenario.holding_period;
        const scenarioLabel = SCENARIO_LABELS[key] ?? key;

        return (
          <Fragment key={key}>
            <tr>
              <td
                rowSpan={2}
                className="px-3 py-1.5 align-middle font-medium text-gray-600"
              >
                {scenarioLabel}
              </td>
              <td className="px-3 py-1.5 text-gray-600">
                What you might get back after costs
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatScenarioAmount(oneYear?.value)}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatScenarioAmount(holding?.value)}
              </td>
            </tr>
            <tr>
              <td className="px-3 py-1.5 text-gray-600">
                Average return each year
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatRiy(oneYear?.return_value)}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatRiy(holding?.return_value)}
              </td>
            </tr>
          </Fragment>
        );
      })}
    </tbody>
  </table>
);

const SriIndicator: FunctionComponent<{ sri: number | null }> = ({ sri }) => (
  <div className="flex items-center gap-2">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
      SRI
    </span>
    <div className="flex items-center gap-1">
      {Array.from({ length: SRI_MAX }, (_, i) => i + 1).map((n) => (
        <span
          key={n}
          className={
            'flex h-6 w-5 items-center justify-center rounded text-[11px] font-semibold ' +
            (sri === n
              ? 'bg-mainblue text-white'
              : 'bg-gray-100 text-gray-400')
          }
        >
          {n}
        </span>
      ))}
    </div>
  </div>
);

interface FigureTableProps {
  caption: string;
  columns: string[];
  rows: { label: string; values: string[] }[];
}

const FigureTable: FunctionComponent<FigureTableProps> = ({
  caption,
  columns,
  rows,
}) => (
  <table className="w-full border-b border-gray-200 text-xs last:border-b-0">
    <thead>
      <tr className="text-gray-500">
        <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide">
          {caption}
        </th>
        {columns.map((col) => (
          <th key={col} className="px-3 py-1.5 text-right font-medium">
            {col}
          </th>
        ))}
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-100 text-gray-800">
      {rows.map((row) => (
        <tr key={row.label}>
          <td className="px-3 py-1.5 font-medium text-gray-600">{row.label}</td>
          {row.values.map((value, i) => (
            <td key={i} className="px-3 py-1.5 text-right tabular-nums">
              {value}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

export function KidFiguresStatusBadge({
  status,
}: {
  status: KidFiguresStatus;
}) {
  return (
    <span
      className={
        'rounded-full px-2 py-1 text-xs font-medium ' +
        KID_FIGURES_STATUS_CLASS[status]
      }
    >
      {KID_FIGURES_STATUS_LABEL[status]}
    </span>
  );
}

export default KidFiguresOverview;
